import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell } from "lucide-react";

/**
 * v1.29.29 — Single bell status indicator for the Party Ledger page.
 * Replaces the old PushSubscribersCard / PushHealthCard banner strips with
 * one compact icon (the underlying notification logic + endpoints are
 * unchanged — only the visual changed):
 *
 *   RED blinking   = party has not enabled notifications (or VAPID missing)
 *   AMBER blinking = notifications enabled, but no push attempts in 7 days
 *   GREEN steady   = notifications enabled and pushes flowing
 *
 * Hover shows the status message. Click (when subscribed) sends a test push,
 * preserving the old card's "Send test push" capability.
 */
export default function LedgerNotifyBell({ partyId, partyName }) {
    const [subs, setSubs] = useState(null);
    const [health, setHealth] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!partyId) { setSubs(null); setHealth(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/admin/parties/${partyId}/push-subscribers`);
                if (!cancelled) setSubs(r.data);
            } catch { if (!cancelled) setSubs(null); }
            try {
                const r = await api.get(`/admin/parties/${partyId}/push-health`);
                if (!cancelled) setHealth(r.data);
            } catch { if (!cancelled) setHealth(null); }
        })();
        return () => { cancelled = true; };
    }, [partyId]);

    if (!partyId || !subs) return null;

    const count = subs.count || 0;
    const attempts = health?.attempts || 0;
    let state, msg;
    if (!subs.vapid_configured) {
        state = "red";
        msg = "Push notifications aren't configured on this deployment (VAPID keys missing) — no phone can receive alerts.";
    } else if (count === 0) {
        state = "red";
        msg = `${partyName || "This party"} hasn't enabled phone notifications yet — ask them to open the portal on their phone and tap "Turn on notifications".`;
    } else if (attempts === 0) {
        state = "amber";
        msg = `Notifications enabled on ${count} device${count === 1 ? "" : "s"}, but no push attempts in the last 7 days. Click to send a test push.`;
    } else {
        const rate = health?.success_rate;
        state = "green";
        msg = `Notifications healthy — ${count} device${count === 1 ? "" : "s"} subscribed · ${health?.success ?? 0}/${attempts} pushes delivered in 7 days${rate != null ? ` (${rate}%)` : ""}. Click to send a test push.`;
    }

    const palette = {
        red: "bg-red-600 text-white animate-pulse",
        amber: "bg-amber-500 text-white animate-pulse",
        green: "bg-emerald-600 text-white",
    }[state];

    const sendTest = async () => {
        if (state === "red" || sending) return;
        setSending(true);
        try {
            const r = await api.post(`/admin/parties/${partyId}/push-test`);
            const n = r.data?.sent || 0;
            if (n > 0) toast.success(`Test push sent to ${n} device${n === 1 ? "" : "s"}. Check the party's phone!`);
            else toast.error("No devices received the push. Ask the customer to open the app and tap 'Turn on notifications'.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test push failed");
        } finally { setSending(false); }
    };

    return (
        <button
            type="button"
            data-testid="ledger-notify-bell"
            data-state={state}
            onClick={sendTest}
            title={msg}
            aria-label={msg}
            className={`fixed right-4 top-1/2 z-40 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full shadow-md ring-2 ring-white transition hover:scale-105 ${palette} ${sending ? "opacity-60" : ""}`}
        >
            <Bell size={15} strokeWidth={2.2} />
        </button>
    );
}
